import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import toast from "react-hot-toast";
import { Api } from "../../services/interceptor";
import "./TickerTemplates.scss";

// Mirrors CricCenterEC2's GraphicsType enum (model/type/GraphicsType.java) - keep in step with it.
// CustomTemplate first/default: a template with no fixed type of its own, fired by its own name as
// a dashboard chip - the most common reason to create one here, so it's the default selection
// rather than buried alphabetically with the ~50 built-in event types.
const GRAPHICS_TYPES = [
  "CustomTemplate",
  "Wicket", "Four", "Six", "RunsChart", "InningsChart", "RunRateChart", "WagonWheel",
  "PartnershipChart", "ExtrasChart", "BoundaryBallPercentageChart", "BatterVsBatterChart",
  "BowlerVsBowlerChart", "PlayersStatsChart", "BatterStats", "BowlerStats",
  "CurrentMatchBatterStats", "CurrentMatchBowlerStats", "PlayingSquad", "TossResult",
  "BattingScoreCard", "BowlingScoreCard", "WeatherInfo", "PointsTable", "ScheduledMatches",
  "NextMatch", "PlayerOut", "DaySummary", "InningEndSummary", "Top10Batsman", "Top10Bowlers",
  "Hatrick", "TeaBreak", "LunchBreak", "BadLights", "BatsmanMultipleOfFifty", "NewBall",
  "HeaderMessage", "NewBatsmanIn", "NewBowlerIn", "ReviewTaken", "DecisionPending", "NotOut",
  "PlayerSubstituted", "ImpactPlayerIn", "StrategicTimeout", "InningBreak", "WinPrediction",
  "Target", "DrsTimer", "CurrentPartnership", "FieldPlacement", "PersonIntro",
];

// Graphic types ticker-overlay's TemplateCardComponent pilot actually checks for a template today.
// A template for any other type is saved and valid - it just isn't rendered anywhere yet.
const WIRED_GRAPHICS_TYPES = new Set(["CustomTemplate", "TeaBreak", "LunchBreak", "BadLights", "BattingScoreCard", "BowlingScoreCard"]);

const EXAMPLE_DEFINITION = JSON.stringify(
  {
    canvas: { width: 1920, height: 1080 },
    cardAnimation: { preset: "slide", direction: "up", durationMs: 900 },
    elements: [
      {
        id: "title",
        type: "text",
        x: 760,
        y: 452,
        w: 400,
        h: 60,
        content: "TEA BREAK",
        fontSize: 32,
        fontWeight: 700,
        color: "#ffffff",
        align: "center",
      },
    ],
  },
  null,
  2,
);

interface TickerTemplateResponse {
  id: number;
  tournamentId: string | null;
  graphicsType: string;
  name: string;
  definitionJson: string;
  active: boolean;
  createdBy: string | null;
  updatedAt: string | null;
}

interface ErrorResponseData {
  message?: string;
}

interface LocationState {
  leagueName?: string;
  tournamentId?: string;
  tournamentName?: string;
}

function readableUpdatedAt(value: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

export default function TickerTemplates() {
  const navigate = useNavigate();
  const { state } = useLocation() as { state?: LocationState };
  const queryClient = useQueryClient();

  const [graphicsType, setGraphicsType] = useState(GRAPHICS_TYPES[0]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [scope, setScope] = useState<"global" | "tournament">("global");
  const [definitionJson, setDefinitionJson] = useState(EXAMPLE_DEFINITION);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const {
    data: templates = [],
    isLoading,
    isError,
  } = useQuery<TickerTemplateResponse[]>({
    queryKey: ["ticker-templates", graphicsType],
    queryFn: async () => {
      const res = await Api.get(`/ticker-templates?graphicsType=${graphicsType}`);
      return res.data;
    },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["ticker-templates", graphicsType] });

  const resetForm = () => {
    setEditingId(null);
    setName("");
    setScope("global");
    setDefinitionJson(EXAMPLE_DEFINITION);
    setFormError(null);
  };

  const loadForEdit = (t: TickerTemplateResponse) => {
    setEditingId(t.id);
    setName(t.name);
    setScope(t.tournamentId ? "tournament" : "global");
    try {
      setDefinitionJson(JSON.stringify(JSON.parse(t.definitionJson), null, 2));
    } catch {
      setDefinitionJson(t.definitionJson);
    }
    setFormError(null);
  };

  const errorMessage = (err: unknown, fallback: string): string => {
    const axiosErr = err as AxiosError<ErrorResponseData>;
    return axiosErr?.response?.data?.message ?? fallback;
  };

  const handleSave = async () => {
    setFormError(null);
    if (!name.trim()) {
      setFormError("Name is required");
      return;
    }
    if (scope === "tournament" && !state?.tournamentId) {
      setFormError("No tournament in context - open this page from a tournament's dashboard to scope a template to it, or choose Global.");
      return;
    }
    try {
      JSON.parse(definitionJson);
    } catch {
      setFormError("definitionJson is not valid JSON");
      return;
    }

    const payload = {
      tournamentId: scope === "tournament" ? state!.tournamentId : null,
      graphicsType,
      name: name.trim(),
      definitionJson,
    };

    setSaving(true);
    try {
      if (editingId) {
        await Api.put(`/ticker-templates/${editingId}`, payload);
        toast.success("Template updated");
      } else {
        await Api.post("/ticker-templates", payload);
        toast.success("Template created");
      }
      resetForm();
      invalidate();
    } catch (err) {
      setFormError(errorMessage(err, "Could not save the template"));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (t: TickerTemplateResponse) => {
    try {
      await Api.patch(`/ticker-templates/${t.id}/active?active=${!t.active}`);
      invalidate();
    } catch (err) {
      toast.error(errorMessage(err, "Could not update the template"));
    }
  };

  const handleDelete = async (t: TickerTemplateResponse) => {
    if (!window.confirm(`Delete "${t.name}"? This can't be undone.`)) return;
    try {
      await Api.delete(`/ticker-templates/${t.id}`);
      if (editingId === t.id) resetForm();
      invalidate();
      toast.success("Template deleted");
    } catch (err) {
      toast.error(errorMessage(err, "Could not delete the template"));
    }
  };

  return (
    <div className="ticker-templates">
      <div className="ticker-templates__header">
        <button type="button" className="ticker-templates__back-btn" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <h1 className="ticker-templates__title">Ticker Templates</h1>
      </div>

      <div className="ticker-templates__selection-info">
        <div className="ticker-templates__info-card">
          <span className="ticker-templates__info-label">League</span>
          <span className="ticker-templates__info-value">{state?.leagueName ?? "—"}</span>
        </div>
        <div className="ticker-templates__info-card">
          <span className="ticker-templates__info-label">Tournament</span>
          <span className="ticker-templates__info-value">{state?.tournamentName ?? "— (global only)"}</span>
        </div>
      </div>

      <div className="ticker-templates__body">
        <aside className="ticker-templates__list-panel">
          <label className="ticker-templates__label" htmlFor="tt-graphics-type">
            Graphic type
          </label>
          <select
            id="tt-graphics-type"
            className="ticker-templates__select"
            value={graphicsType}
            onChange={(e) => {
              setGraphicsType(e.target.value);
              resetForm();
            }}
          >
            {GRAPHICS_TYPES.map((g) => (
              <option key={g} value={g}>
                {g}
                {WIRED_GRAPHICS_TYPES.has(g) ? "" : " (not rendered yet)"}
              </option>
            ))}
          </select>
          {graphicsType === "CustomTemplate" && (
            <p className="ticker-templates__hint ticker-templates__hint--inline">
              Shows up as its own chip in the dashboard, labeled by the Name you give it below — not tied to any
              existing ticker trigger.
            </p>
          )}

          <button type="button" className="ticker-templates__new-btn" onClick={resetForm}>
            + New template
          </button>

          {isLoading && <p className="ticker-templates__hint">Loading…</p>}
          {isError && <p className="ticker-templates__error">Couldn't load templates - is /ticker-templates deployed on this environment?</p>}
          {!isLoading && !isError && templates.length === 0 && <p className="ticker-templates__hint">No templates for this type yet.</p>}

          <ul className="ticker-templates__list">
            {templates.map((t) => (
              <li key={t.id} className={`ticker-templates__item${editingId === t.id ? " ticker-templates__item--active" : ""}`}>
                <button type="button" className="ticker-templates__item-main" onClick={() => loadForEdit(t)}>
                  <span className="ticker-templates__item-name">{t.name}</span>
                  <span className="ticker-templates__item-scope">{t.tournamentId ? `Tournament ${t.tournamentId}` : "Global"}</span>
                  {t.updatedAt && <span className="ticker-templates__item-updated">{readableUpdatedAt(t.updatedAt)}</span>}
                </button>
                <div className="ticker-templates__item-actions">
                  <button
                    type="button"
                    className={`ticker-templates__toggle${t.active ? " ticker-templates__toggle--on" : ""}`}
                    onClick={() => handleToggleActive(t)}
                  >
                    {t.active ? "Active" : "Inactive"}
                  </button>
                  <button type="button" className="ticker-templates__delete-btn" onClick={() => handleDelete(t)}>
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </aside>

        <section className="ticker-templates__form-panel">
          <h2 className="ticker-templates__form-title">{editingId ? "Edit template" : "New template"}</h2>

          <label className="ticker-templates__label" htmlFor="tt-name">
            Name
          </label>
          <input
            id="tt-name"
            className="ticker-templates__input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Tea Break — default"
          />

          <label className="ticker-templates__label">Scope</label>
          <div className="ticker-templates__scope-row">
            <label className="ticker-templates__radio">
              <input type="radio" name="tt-scope" checked={scope === "global"} onChange={() => setScope("global")} />
              Global default
            </label>
            <label className="ticker-templates__radio">
              <input
                type="radio"
                name="tt-scope"
                checked={scope === "tournament"}
                onChange={() => setScope("tournament")}
                disabled={!state?.tournamentId}
              />
              This tournament{state?.tournamentName ? ` (${state.tournamentName})` : ""}
            </label>
          </div>

          <label className="ticker-templates__label" htmlFor="tt-definition">
            definitionJson
          </label>
          <textarea
            id="tt-definition"
            className="ticker-templates__textarea"
            value={definitionJson}
            onChange={(e) => setDefinitionJson(e.target.value)}
            spellCheck={false}
          />

          {formError && <p className="ticker-templates__error">{formError}</p>}

          <div className="ticker-templates__form-actions">
            {editingId && (
              <button type="button" className="ticker-templates__btn-cancel" onClick={resetForm}>
                Cancel edit
              </button>
            )}
            <button type="button" className="ticker-templates__btn-save" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : editingId ? "Save changes" : "Create template"}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
